import DateHelper from '../../../Utility/DateHelper';
import { Random } from '../../../Utility/Random';
import { gameTracking } from '../../../Utility/Tracking/FBLegacyTracking';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { IGameState } from '../../Game/IGameState';
import { setHelperFriendAttempts } from '../../Player/PlayerLevelHelper';
import { getFriendData } from '../../Player/PlayerStateHelper';
import { HelperFriendStatus } from './HelperFriendStatus';
import Game from '../../../Game';

export function checkHelperFriendVisible(state: IGameState): IGameState {
    // Must be at least at level 15.
    if (state.levelState.levelNumber < 15) return state;

    let helperFriendStatus: HelperFriendStatus =
        state.levelState.helperFriendStatus;

    // If helper-friends are disabled for this level then do nothing.
    if (
        helperFriendStatus === HelperFriendStatus.DISABLED ||
        helperFriendStatus === HelperFriendStatus.USED
    ) {
        return state;
    }

    // Helper-friends is only available if 1 to 5 cards remain in the players stack.
    if (
        state.levelState.cardsState.inStack.length <= 5 &&
        state.levelState.cardsState.inStack.length > 0
    ) {
        if (helperFriendStatus === HelperFriendStatus.ENABLED) {
            helperFriendStatus = HelperFriendStatus.SHOW;
            gameTracking(FBTrackingEvent.Helper_Show);
        }
    } else {
        // If helper-friends was visible and not used then it be set back to 'ENABLED'.
        // It will then re-appear if the user has 1 to 5 cards again.
        if (helperFriendStatus === HelperFriendStatus.SHOW) {
            helperFriendStatus = HelperFriendStatus.ENABLED;
        }
    }

    return {
        ...state,
        levelState: {
            ...state.levelState,
            helperFriendStatus: helperFriendStatus,
        },
    };
}

export function checkHelperFriendEnabled(state: IGameState): IGameState {
    // If social features are not supported helper-friend will always be disabled.
    if (!Game.sdk.driver.hasSocialFeaturesSupport()) {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                helperFriendStatus: HelperFriendStatus.DISABLED,
            },
        };
    }

    // If the user has never used a helper-friend before (no friends with helper-friend-timers) helper-friend will
    // always be enabled.
    if (
        Game.sdk.driver.hasSocialFeaturesSupport() &&
        !state.playerState.friendData.some(
            friend => friend.helperTimer !== undefined
        )
    ) {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                helperFriendStatus: HelperFriendStatus.ENABLED,
            },
        };
    }

    // Otherwise calculate the percentage chance based on the number of level attempts.
    const levels = state.playerState.levels[state.levelState.levelType];
    const currentLevel = levels.find(
        level => level.levelNumber === state.levelState.levelNumber
    );

    // 10% chance per attempt on this level since, per session, since helper-friend was last used.
    const percentageChance = Math.min(
        0.4,
        (currentLevel.helperFriendAttempts - 1) * 0.1
    );

    if (Random.value() <= percentageChance) {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                helperFriendStatus: HelperFriendStatus.ENABLED,
            },
        };
    }

    return {
        ...state,
        levelState: {
            ...state.levelState,
            helperFriendStatus: HelperFriendStatus.DISABLED,
        },
    };
}

/**
 * Handle a successful 'help friend'.
 *
 * The cooldown validation for helper-friends is currently done in `LeaderboardHelper.getFriendForHelp()`.
 * This is because new 'unknown' friends are not initialized into the state when the game is loaded.
 * TODO: Move all 'platform friends' to state during initialization/preload.
 *
 * @param state
 * @param friendId The playerId of the friend.
 */
export function helpFriend(state: IGameState, friendId: string): IGameState {
    // Reset helper-friend attempts for this level
    state = setHelperFriendAttempts(
        state,
        state.levelState.levelNumber,
        state.levelState.levelType,
        0
    );

    const { playerState, friendData } = getFriendData(
        state.playerState,
        friendId
    );

    return {
        ...state,
        playerState: {
            ...playerState,
            friendData: playerState.friendData.map(friend => {
                if (friend.playerId !== friendId) {
                    return friend;
                }
                return {
                    ...friend,
                    helperTimer: DateHelper.now().toString(),
                };
            }),
        },
        levelState: {
            ...state.levelState,
            helperFriendStatus: HelperFriendStatus.USED,
        },
    };
}
